import React, {useState, useEffect} from 'react';
import {useParams, useNavigate, Link as RouterLink} from 'react-router-dom';
import {
    Box, Typography, Paper, CircularProgress, Grid, Button, Alert,
    TextField, FormControlLabel, Switch, Snackbar, Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiService from '../../services/apiService';
import ImageUploader from '../../components/ImageUploader';
import EntityCriteriaEditor from '../../components/EntityCriteriaEditor';
import JsonEditor from '../../components/JsonEditor';
import HiLoConfigEditor from '../../components/HiLoConfigEditor';
import DiceProbabilityPreview from '../../components/DiceProbabilityPreview';
import {ProtectedFieldSet} from '../../context/AuthDisabledContext';
import Protected from '../../components/Protected';
import {ROLES} from '../../config/roles';

const MinigameEditPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';
    const requiredAction = isNew ? 'create' : 'update';

    const [identifier, setIdentifier] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [iconUrl, setIconUrl] = useState('');
    const [enabled, setEnabled] = useState(true);
    const [visible, setVisible] = useState(true);
    const [entryCost, setEntryCost] = useState([]);
    const [dailyPlayLimit, setDailyPlayLimit] = useState('');
    const [cooldownSeconds, setCooldownSeconds] = useState('');
    const [minPlayerLevel, setMinPlayerLevel] = useState(0);
    const [availableFrom, setAvailableFrom] = useState('');
    const [availableUntil, setAvailableUntil] = useState('');
    const [config, setConfig] = useState({});

    const [loading, setLoading] = useState(!isNew);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [snackbar, setSnackbar] = useState({open: false, message: ''});

    useEffect(() => {
        if (!isNew) {
            setLoading(true);
            apiService.get(`/api/minigames/${id}`)
                .then(data => {
                    setIdentifier(data.identifier || '');
                    setName(data.name || '');
                    setDescription(data.description || '');
                    setIconUrl(data.iconUrl || '');
                    setEnabled(data.enabled ?? true);
                    setVisible(data.visible ?? true);
                    setEntryCost(data.entryCost || []);
                    setDailyPlayLimit(data.dailyPlayLimit ?? '');
                    setCooldownSeconds(data.cooldownSeconds ?? '');
                    setMinPlayerLevel(data.minPlayerLevel ?? 0);
                    setAvailableFrom(data.availableFrom ? data.availableFrom.substring(0, 16) : '');
                    setAvailableUntil(data.availableUntil ? data.availableUntil.substring(0, 16) : '');
                    setConfig(data.config || {});
                })
                .catch(err => setError(err.message || 'Failed to load minigame data'))
                .finally(() => setLoading(false));
        }
    }, [id, isNew]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitted(true);
        setError(null);

        const entryCostError = EntityCriteriaEditor.validate(entryCost);
        if (entryCostError) {
            setError('Entry Cost: ' + entryCostError);
            return;
        }

        if (identifier === 'hilo') {
            const hiloError = HiLoConfigEditor.validate(config);
            if (hiloError) {
                setError('Config: ' + hiloError);
                return;
            }
        }

        if (identifier === 'dice_rolling') {
            const diceError = DiceProbabilityPreview.validate(config);
            if (diceError) {
                setError('Config: ' + diceError);
                return;
            }
        }

        setIsSaving(true);

        const data = {
            id: isNew ? null : parseInt(id),
            identifier, name, description, iconUrl, enabled, visible,
            entryCost,
            dailyPlayLimit: dailyPlayLimit !== '' ? parseInt(dailyPlayLimit) : null,
            cooldownSeconds: cooldownSeconds !== '' ? parseInt(cooldownSeconds) : null,
            minPlayerLevel: minPlayerLevel ? parseInt(minPlayerLevel) : 0,
            availableFrom: availableFrom ? availableFrom + ':00Z' : null,
            availableUntil: availableUntil ? availableUntil + ':00Z' : null,
            config,
        };

        try {
            const apiCall = isNew
                ? apiService.post('/api/minigames', data)
                : apiService.put(`/api/minigames/${id}`, data);
            await apiCall;
            setSnackbar({open: true, message: 'Minigame saved successfully!'});
            setTimeout(() => navigate('/minigames'), 800);
        } catch (err) {
            setError(err.message || 'Failed to save minigame');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;

    return (
        <Box sx={{p: 2}} component="form" onSubmit={handleSubmit}>
            <Typography variant="h4" gutterBottom>
                {isNew ? 'Create Minigame' : `Edit Minigame: ${name}`}
            </Typography>

            <Protected component={MinigameEditPage} action={requiredAction} mode="disable">
                <ProtectedFieldSet>
                    <Grid container spacing={3}>
                        <Grid size={{xs: 12, md: 6}}>
                            <Stack spacing={3}>
                                <Paper sx={{p: 2}}>
                                    <Typography variant="h6" gutterBottom>Basic Configuration</Typography>
                                    <Stack spacing={2}>
                                        <TextField required fullWidth label="Identifier" value={identifier}
                                                   onChange={e => setIdentifier(e.target.value)}/>
                                        <TextField required fullWidth label="Name" value={name}
                                                   onChange={e => setName(e.target.value)}/>
                                        <TextField fullWidth multiline rows={4} label="Description" value={description}
                                                   onChange={e => setDescription(e.target.value)}/>
                                        <FormControlLabel
                                            control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)}/>}
                                            label="Enabled"/>
                                        <FormControlLabel
                                            control={<Switch checked={visible} onChange={e => setVisible(e.target.checked)}/>}
                                            label="Visible"/>
                                    </Stack>
                                </Paper>

                                <Paper sx={{p: 2}}>
                                    <Typography variant="h6" gutterBottom>Rate Limiting</Typography>
                                    <Stack spacing={2}>
                                        <TextField fullWidth type="number" label="Daily Play Limit"
                                                   value={dailyPlayLimit}
                                                   onChange={e => setDailyPlayLimit(e.target.value)}
                                                   helperText="Leave empty for unlimited"/>
                                        <TextField fullWidth type="number" label="Cooldown (seconds)"
                                                   value={cooldownSeconds}
                                                   onChange={e => setCooldownSeconds(e.target.value)}
                                                   helperText="Leave empty for no cooldown"/>
                                        <TextField fullWidth type="number" label="Min Player Level"
                                                   value={minPlayerLevel}
                                                   onChange={e => setMinPlayerLevel(e.target.value)}/>
                                    </Stack>
                                </Paper>

                                <Paper sx={{p: 2}}>
                                    <Typography variant="h6" gutterBottom>Time Gating</Typography>
                                    <Stack spacing={2}>
                                        <TextField fullWidth type="datetime-local" label="Available From"
                                                   value={availableFrom}
                                                   onChange={e => setAvailableFrom(e.target.value)}
                                                   slotProps={{inputLabel: {shrink: true}}}
                                                   helperText="Leave empty for always available"/>
                                        <TextField fullWidth type="datetime-local" label="Available Until"
                                                   value={availableUntil}
                                                   onChange={e => setAvailableUntil(e.target.value)}
                                                   slotProps={{inputLabel: {shrink: true}}}
                                                   helperText="Leave empty for no end date"/>
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Grid>

                        <Grid size={{xs: 12, md: 6}}>
                            <ImageUploader
                                imageUrl={iconUrl}
                                onImageUrlChange={setIconUrl}
                                uploadPath="Minigame"
                                baseFileName={name}
                                isUploadEnabled={!!name}
                            />
                        </Grid>
                    </Grid>

                    <Stack spacing={3} sx={{mt: 3}}>
                        <Paper sx={{p: 2}}>
                            <Typography variant="h6" gutterBottom>Entry Cost</Typography>
                            <EntityCriteriaEditor
                                value={entryCost}
                                onChange={setEntryCost}
                                preset="entryCost"
                                showErrors={submitted}
                            />
                        </Paper>

                        <Paper sx={{p: 2}}>
                            <Typography variant="h6" gutterBottom>Game Configuration</Typography>
                            {identifier === 'hilo' ? (
                                <HiLoConfigEditor value={config} onChange={setConfig}/>
                            ) : (
                                <>
                                    <JsonEditor value={config} onChange={setConfig} rootName="config" collapsed={2}/>
                                    {identifier === 'dice_rolling' && <DiceProbabilityPreview config={config}/>}
                                </>
                            )}
                        </Paper>
                    </Stack>

                    {error && <Alert severity="error" sx={{mt: 3}}>{error}</Alert>}

                    <Stack direction="row" spacing={2} sx={{mt: 3}}>
                        <Protected component={MinigameEditPage} action={requiredAction}>
                            <Button type="submit" variant="contained"
                                    startIcon={isSaving ? <CircularProgress size={20}/> : <SaveIcon/>}
                                    disabled={isSaving}>
                                {isSaving ? 'Saving...' : 'Save Minigame'}
                            </Button>
                        </Protected>
                        <Button component={RouterLink} to="/minigames" variant="outlined" startIcon={<ArrowBackIcon/>}>
                            Back to List
                        </Button>
                    </Stack>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({open: false, message: ''})}
                        message={snackbar.message}
                    />
                </ProtectedFieldSet>
            </Protected>
        </Box>
    );
};

MinigameEditPage.abilities = {
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
};

export default MinigameEditPage;
